import React, { useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import Icon1 from "@assets/icons/Icon";
import { ordresApi } from "@services/api/ordres";
import { dossiersApi } from "@services/api/dossiers";

// react-pdf ships no worker of its own — same CDN worker as the signature editor.
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// pdf.js asks for byte ranges by default, which makes the browser preflight the
// request with a `Range` header — the storage bucket's CORS rules don't allow it,
// so every thumbnail would fail then retry. Fetch the file in one go instead:
// documents are small and we only ever draw page 1. Module-level constant because
// react-pdf re-loads the document whenever this object's identity changes.
const PDF_OPTIONS = { disableRange: true, disableStream: true };

export const IMAGE_EXTENSIONS = /\.(jpg|jpeg|png|webp|gif|heic|bmp|svg)$/i;

export type ThumbDoc = { id: string; name: string; type?: string; gcs_path?: string };

export function isImageDoc(d: ThumbDoc) {
  return d.type === "image" || IMAGE_EXTENSIONS.test(d.gcs_path || d.name);
}

export function isPdfDoc(d: ThumbDoc) {
  return d.type === "pdf" || /\.pdf$/i.test(d.gcs_path || d.name);
}

/** Où est rangé le document : l'ordre ou le dossier qui le liste. */
export type DocOwner = { ordreId?: string; dossierId?: string };

/** Signed URL of a document, resolved from its gcs_path: `ordres/{org}/{ordreId}/…`
 *  and `dossiers/{org}/{dossierId}/…` are served by two different endpoints. Any
 *  other path (photo reçue sur le chat : `orgs/{org}/messagerie/…`) ne dit pas à qui
 *  elle appartient : on passe par le propriétaire connu de l'écran — sans lui, la
 *  vignette interrogeait « le dossier » {org} et restait vide. */
export function resolveDocumentUrl(doc: { id: string; gcs_path?: string }, owner: DocOwner = {}): Promise<string> {
  const path = doc.gcs_path || "";
  const pathId = path.split("/")[2];
  const { ordreId, dossierId } = owner;
  if (path.startsWith("ordres/")) return ordresApi.getDocumentUrl(pathId, doc.id);
  if (path.startsWith("dossiers/")) return dossiersApi.getDocumentUrl(pathId, doc.id);
  if (dossierId) {
    return dossiersApi.getDocumentUrl(dossierId, doc.id)
      .catch((e) => (ordreId ? ordresApi.getDocumentUrl(ordreId, doc.id) : Promise.reject(e)));
  }
  if (ordreId) return ordresApi.getDocumentUrl(ordreId, doc.id);
  return Promise.reject(new Error("Propriétaire du document inconnu"));
}

export function useDocumentUrl(doc: ThumbDoc, enabled = true, owner: DocOwner = {}): string | null {
  const [url, setUrl] = useState<string | null>(null);
  const { ordreId, dossierId } = owner;
  useEffect(() => {
    if (!enabled || !doc.gcs_path) return;
    let cancelled = false;
    resolveDocumentUrl(doc, { ordreId, dossierId })
      .then((u) => { if (!cancelled) setUrl(u); })
      .catch(() => { /* keep the icon */ });
    return () => { cancelled = true; };
  }, [doc.gcs_path, doc.id, enabled, ordreId, dossierId]);
  return url;
}

/** True once the element is (nearly) on screen. A dossier can list dozens of
 *  documents and each PDF is downloaded whole to be rendered, so nothing is
 *  fetched before the tile is actually about to be seen. */
function useOnScreen(ref: React.RefObject<HTMLElement | null>): boolean {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || visible) return;
    if (typeof IntersectionObserver === "undefined") { setVisible(true); return; }
    const io = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) setVisible(true); },
      { rootMargin: "200px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref, visible]);
  return visible;
}

/** Square preview of a document: the picture for an image, the first page for a
 *  PDF, a file icon for anything else (or when the preview can't be produced). */
export function DocThumbnail({
  doc,
  size = 120,
  onClick,
  className = "",
  ordreId,
  dossierId,
}: {
  doc: ThumbDoc;
  /** Propriétaire du document, pour les chemins qui ne le disent pas (photos du chat). */
  ordreId?: string;
  dossierId?: string;
  /** Side of the square tile, in px. */
  size?: number;
  onClick?: () => void;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const visible = useOnScreen(ref);
  const image = isImageDoc(doc);
  const pdf = !image && isPdfDoc(doc);
  const [pdfFailed, setPdfFailed] = useState(false);
  const url = useDocumentUrl(doc, visible && (image || pdf) && !pdfFailed, { ordreId, dossierId });

  const fallback = (
    <Icon1 name={pdf ? "file" : "image"} size={Math.max(16, Math.round(size / 5))} color="#D4D4D4" />
  );

  return (
    <div
      ref={ref}
      onClick={onClick}
      style={{ width: size, height: size }}
      className={`rounded-lg overflow-hidden bg-neutral-100 flex items-center justify-center flex-shrink-0${
        onClick ? " cursor-pointer hover:opacity-80 transition-opacity" : ""
      } ${className}`}
    >
      {image && url ? (
        <img src={url} alt={doc.name} className="w-full h-full object-cover" loading="lazy" />
      ) : pdf && url && !pdfFailed ? (
        // A4 is taller than wide: render at the tile's width and crop the bottom,
        // so the top of the page (header, title, amounts) is what shows.
        <div className="w-full h-full overflow-hidden flex justify-center items-start bg-white">
          <Document
            file={url}
            options={PDF_OPTIONS}
            loading={fallback}
            error={fallback}
            noData={fallback}
            onLoadError={() => setPdfFailed(true)}
          >
            <Page
              pageNumber={1}
              width={size}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              loading={fallback}
              error={fallback}
            />
          </Document>
        </div>
      ) : (
        fallback
      )}
    </div>
  );
}
